import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

/**
 * Dropdown chọn 1 Site (HO, VTF, VTF1, ...) — options tải 1 lần qua `fetcher()`
 * (GET .../site_options → [{ value, label }]). Không có SiteSelect dùng chung nào
 * sẵn trong repo (NotificationRulesPanel của Payment Request build inline) nên đây
 * là component mới, dùng chung cho Purchase Requisition (chọn site khi tạo phiếu)
 * và Settings (Financial Controller/GM theo site).
 * Props:
 *   value: number|string|null (site_id)
 *   onChange(siteId|null)
 *   fetcher(): Promise<{ data: [{ value, label }] }>
 *   placeholder
 */
const SiteSelect = ({ value, onChange, fetcher, placeholder = "Select site..." }) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [coords, setCoords] = useState(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);

  // Tải options khi mở dropdown HOẶC khi đã có value sẵn (prefill) — để hiện đúng
  // nhãn site đã chọn ngay, không bắt user mở dropdown/chọn lại.
  useEffect(() => {
    if (loaded || !fetcher) return;
    const hasValue = value !== null && value !== undefined && value !== "";
    if (!open && !hasValue) return;
    (async () => {
      try {
        const res = await fetcher();
        setOptions(res?.data || []);
      } catch {
        setOptions([]);
      } finally {
        setLoaded(true);
      }
    })();
  }, [open, loaded, fetcher, value]);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return undefined;
    const compute = () => {
      const r = btnRef.current.getBoundingClientRect();
      setCoords({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    compute();
    const onMove = () => compute();
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => {
      if (!btnRef.current?.contains(e.target) && !popRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const current = options.find((o) => String(o.value) === String(value));

  return (
    <>
      <div
        ref={btnRef}
        role="button"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        className="tw-w-full tw-h-10 tw-flex tw-items-center tw-gap-2 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-bg-geist-bg tw-px-3 tw-text-sm tw-cursor-pointer hover:tw-border-geist-border-2 focus-within:tw-border-geist-accent tw-transition-colors"
      >
        <i className="bi bi-geo-alt tw-text-[12px] tw-text-geist-fg-3 tw-flex-shrink-0" />
        <span className={`tw-flex-1 tw-min-w-0 tw-truncate ${current ? "tw-text-geist-fg" : "tw-text-geist-fg-3"}`}>
          {current ? current.label : placeholder}
        </span>
        {current && (
          <i
            role="button"
            title="Clear"
            className="bi bi-x-lg tw-text-[11px] tw-text-geist-fg-3 hover:tw-text-geist-error"
            onClick={(e) => { e.stopPropagation(); onChange(null); }}
          />
        )}
        <i className={`bi ${open ? "bi-chevron-up" : "bi-chevron-down"} tw-text-[11px] tw-text-geist-fg-3 tw-shrink-0`} />
      </div>

      {open && coords && createPortal(
        <div
          ref={popRef}
          style={{ position: "fixed", top: coords.top, left: coords.left, width: coords.width, minWidth: 200, zIndex: 100000 }}
          className="geist-scope tw-bg-geist-bg tw-border tw-border-solid tw-border-geist-border tw-rounded-geist-md tw-shadow-geist-menu tw-overflow-hidden"
        >
          <div className="tw-max-h-60 tw-overflow-y-auto tw-py-1">
            {!loaded ? (
              <div className="tw-px-3 tw-py-3 tw-text-xs tw-text-geist-fg-3 tw-flex tw-items-center tw-gap-2">
                <i className="bi bi-arrow-repeat tw-animate-spin" /> Loading...
              </div>
            ) : options.length === 0 ? (
              <div className="tw-px-3 tw-py-4 tw-text-xs tw-text-geist-fg-3 tw-text-center">No sites.</div>
            ) : (
              options.map((o) => {
                const selected = String(o.value) === String(value);
                return (
                  <div
                    key={o.value}
                    role="button"
                    tabIndex={0}
                    onClick={() => { onChange(o.value); setOpen(false); }}
                    className={`tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-cursor-pointer tw-text-sm tw-transition-colors ${selected ? "tw-bg-geist-100 tw-text-geist-fg tw-font-medium" : "tw-text-geist-fg-2 hover:tw-bg-geist-100"}`}
                  >
                    <span className="tw-flex-1 tw-min-w-0 tw-truncate">{o.label}</span>
                    {selected && <i className="bi bi-check-lg tw-text-geist-accent tw-text-sm tw-shrink-0" />}
                  </div>
                );
              })
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

SiteSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  fetcher: PropTypes.func,
  placeholder: PropTypes.string,
};

export default SiteSelect;
